import type { WindIndex, WindModelInfo } from '../tiles/format';
import type { LngLatBounds, ModelGrid } from '../tiles/grid';
import { blocksCovering, gridContains, levelGrid, type BlockRef } from '../tiles/levels';

/** What the map shows: the area to cover (some margin included) and the zoom (MapLibre, 512 px tiles). */
export interface ViewState {
  bounds: LngLatBounds;
  zoom: number;
}

/**
 * Screen px a grid cell must span to be worth its data: finer levels would add detail nobody can see, at the
 * cost of more blocks. The level a model is shown at depends on this and the zoom only.
 */
const MIN_CELL_PX = 4;

/** The valid times around a time, and how far between them it lies (0 = a, 1 = b). */
export interface TimeBracket {
  a: number;
  b: number;
  t: number;
}

/** One model to load and show for a view. */
export interface ModelPlan {
  model: WindModelInfo;
  /** ms since the epoch. */
  run: number;
  level: number;
  /** The model's grid at `level`. */
  grid: ModelGrid;
  times: TimeBracket;
  blocks: BlockRef[];
}

/**
 * The valid times around `time`: the nearest one alone before the first or after the last. Null without
 * valid times.
 */
export function bracketTime(times: readonly number[], time: number): TimeBracket | null {
  if (times.length === 0) return null;
  if (time <= times[0]) return { a: times[0], b: times[0], t: 0 };
  const last = times[times.length - 1];
  if (time >= last) return { a: last, b: last, t: 0 };
  let k = 1;
  while (times[k] < time) k++;
  if (times[k] === time) return { a: time, b: time, t: 0 };
  return { a: times[k - 1], b: times[k], t: (time - times[k - 1]) / (times[k] - times[k - 1]) };
}

/** Screen px a native cell of `grid` spans at `zoom`, around latitude `lat`. */
export function cellPx(grid: ModelGrid, zoom: number, lat: number): number {
  const pxPerDegree = (512 * 2 ** zoom) / 360;
  // Metres become degrees of longitude, which Web Mercator draws the same size at any latitude.
  const degrees = grid.kind === 'latlon' ? grid.dLon : grid.dx / (111_195 * Math.cos((lat * Math.PI) / 180));
  return degrees * pxPerDegree;
}

/**
 * The level at which a model's cells span at least MIN_CELL_PX. Null when even its coarsest level is finer
 * than that, unless `clamp` (for the world's base model, which must always show).
 */
export function levelFor(model: WindModelInfo, zoom: number, lat: number, clamp = false): number | null {
  const level = Math.max(0, Math.ceil(Math.log2(MIN_CELL_PX / cellPx(model.grid, zoom, lat))));
  if (level < model.levels) return level;
  return clamp ? model.levels - 1 : null;
}

const isGlobal = (m: WindModelInfo) => m.grid.kind === 'latlon' && m.grid.wraps;

/**
 * The models to show for a view, coarse to fine, each with its level, blocks and valid times around `time`.
 * The finest global model is the base everywhere; regional models overlapping the view come on top, those
 * hidden behind a finer one covering the whole view left out.
 */
export function planView(index: WindIndex, view: ViewState, time: number): ModelPlan[] {
  const lat = (view.bounds.s + view.bounds.n) / 2;
  const base = index.models.filter(isGlobal).sort((a, b) => a.km - b.km)[0];
  const regional = index.models.filter((m) => !isGlobal(m)).sort((a, b) => b.km - a.km);

  const plans: ModelPlan[] = [];
  for (const model of base ? [base, ...regional] : regional) {
    const level = levelFor(model, view.zoom, lat, model === base);
    if (level === null) continue;
    const times = bracketTime(model.times.map(Date.parse), time);
    if (!times) continue;
    const grid = levelGrid(model.grid, level);
    const blocks = blocksCovering(grid, view.bounds);
    if (blocks.length === 0) continue;
    plans.push({ model, run: Date.parse(model.run), level, grid, times, blocks });
  }
  // A regional model covering the whole view hides every coarser one but the base (the fallback while loading).
  const top = plans.findLastIndex((p) => p.model !== base && gridContains(p.model.grid, view.bounds));
  return top <= 0 ? plans : plans.filter((p, k) => p.model === base || k >= top);
}
